import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, ShieldCheck, Wrench, AlertTriangle } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import QuoteButton, { EURO_CAUTION } from "@/components/site/QuoteButton";
import { SITE } from "@/site.config";

const setMeta = (title, desc, canonical) => {
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "description");
    document.head.appendChild(m);
  }
  m.setAttribute("content", desc);
  let c = document.querySelector('link[rel="canonical"]');
  if (!c) {
    c = document.createElement("link");
    c.setAttribute("rel", "canonical");
    document.head.appendChild(c);
  }
  c.setAttribute("href", canonical);
};

export default function AboutPage() {
  useEffect(() => {
    setMeta(
      "About Powerz Auto Electrical | Kunda Park Sunshine Coast Specialist Workshop",
      "Powerz Auto Electrical is a specialist auto electrical workshop at Kunda Park on the Sunshine Coast. Registered Australian company, ARC-licensed, 10+ years hands-on, 95+ five-star Google reviews. Towing electrics specialists — REDARC Tow-Pro, 12 pin & Anderson plug installs.",
      "https://powerzautoelectrical.com.au/about"
    );
    window.scrollTo(0, 0);
  }, []);

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "AutomotiveBusiness",
    name: SITE.business.name,
    url: "https://powerzautoelectrical.com.au/about",
    telephone: SITE.business.phone,
    email: SITE.business.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.business.address.line1,
      addressLocality: SITE.business.address.suburb,
      addressRegion: SITE.business.address.state,
      postalCode: SITE.business.address.postcode,
      addressCountry: SITE.business.address.country,
    },
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Sunshine Coast, Queensland, Australia",
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://powerzautoelectrical.com.au/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "About",
        item: "https://powerzautoelectrical.com.au/about",
      },
    ],
  };

  return (
    <div className="bg-black text-white min-h-screen">
      <Header />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      {/* Breadcrumbs */}
      <nav
        aria-label="Breadcrumb"
        data-testid="about-breadcrumbs"
        className="max-w-7xl mx-auto px-5 lg:px-8 pt-24 pb-4"
      >
        <ol className="flex items-center gap-2 text-xs barlow text-white/40 tracking-wide">
          <li>
            <Link to="/" className="hover:text-cyan-pz">
              HOME
            </Link>
          </li>
          <li>/</li>
          <li className="text-cyan-pz">ABOUT</li>
        </ol>
      </nav>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-6 pb-16">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-10 bg-cyan-pz" />
          <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
            ABOUT POWERZ
          </span>
        </div>
        <h1
          data-testid="about-h1"
          className="bebas text-white text-5xl md:text-7xl leading-[0.9] max-w-4xl"
        >
          A SPECIALIST AUTO ELECTRICAL WORKSHOP
          <br />
          <span className="text-cyan-pz">AT KUNDA PARK.</span>
        </h1>
        <p className="barlow text-white/70 max-w-3xl mt-6 text-lg">
          Powerz Auto Electrical is a registered Australian company (Pty Ltd)
          and specialist auto electrical workshop based at 2a/3 Hitech Dr,
          Kunda Park — with customers driving in from every Sunshine Coast suburb,
          Caloundra to Noosa. Towing electrics is our core specialty: REDARC Tow-Pro brake
          controllers, 12 pin trailer plugs and Anderson plugs, fitted on the
          tow vehicle and tested on a trailer before you leave. We're the
          workshop you send your Hilux, LandCruiser, Ranger, Amarok, Everest
          or work ute to when the job needs to be done properly the first time.
        </p>
      </section>

      {/* Facts strip — real E-E-A-T signals only */}
      <section className="border-y border-white/10 bg-[#0A0A0A] py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {[
            { k: "10+ YEARS", v: "of hands-on auto electrical experience in the workshop" },
            { k: "95+", v: "five-star Google reviews from real Sunshine Coast customers" },
            { k: "12 MONTH", v: "workmanship warranty on every install, in writing" },
            { k: "ARC", v: "licensed workshop — registered Australian company (Pty Ltd)" },
          ].map((s) => (
            <div key={s.k} data-testid={`about-fact-${s.k}`}>
              <p className="bebas text-cyan-pz text-2xl md:text-4xl tracking-wider">
                {s.k}
              </p>
              <p className="barlow text-white/60 text-sm mt-1">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Body copy */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16 space-y-8">
        <div>
          <h2 className="bebas text-cyan-pz tracking-widest text-sm mb-2">
            WHAT WE SPECIALISE IN
          </h2>
          <p className="barlow text-white/70 leading-relaxed">
            Powerz Auto Electrical is a specialist workshop — not a general
            auto electrician trying to be everything to everyone. We focus on
            three areas: <strong className="text-white">auto electrical
            diagnostics and repair</strong>, <strong className="text-white">4WD
            and towing electrical installations</strong>, and <strong className="text-white">auto
            air conditioning</strong>. Under-bonnet dual battery, REDARC Tow-Pro
            electric brake controllers, Anderson plugs, UHF radios, driving
            lights and behind-the-scenes fault diagnostics. Done properly.
            First time.
          </p>
        </div>

        <div>
          <h2 className="bebas text-cyan-pz tracking-widest text-sm mb-2">
            WHAT WE DON'T DO
          </h2>
          <p className="barlow text-white/70 leading-relaxed">
            Powerz Auto Electrical works on <strong className="text-white">vehicles
            only</strong>. Our work on any tow vehicle stops at the trailer plug
            or the Anderson plug. We do <strong className="text-white">not</strong>
            {" "}wire caravan internals, camper vans, or off-grid / touring house
            banks. We do <strong className="text-white">not</strong> install
            lithium battery systems, MPPT solar regulators or inverters. We do
            <strong className="text-white"> not</strong> mount dual batteries
            behind seats, in cargo areas or in canopies — every dual battery we
            fit sits under the bonnet in the factory tray location. Firm
            boundaries mean we do the work we specialise in better than a
            generalist ever could.
          </p>
        </div>

        <div>
          <h2 className="bebas text-cyan-pz tracking-widest text-sm mb-2">
            WHERE WE'RE BASED
          </h2>
          <p className="barlow text-white/70 leading-relaxed">
            Our workshop is at{" "}
            <strong className="text-white">
              {SITE.business.address.full}
            </strong>
            . Five minutes off the Bruce Highway — easy from Maroochydore,
            Mooloolaba, Caloundra, Noosa, Buderim, Sippy Downs, Coolum and
            Peregian. Every job is booked into a dedicated workshop day, quoted
            in writing before it starts, and handed back with a 12-month
            workmanship warranty. Note: Powerz previously operated from Coolum
            Beach — Kunda Park is our current workshop.
          </p>
        </div>

        <div>
          <h2 className="bebas text-cyan-pz tracking-widest text-sm mb-2">
            OUR STANDARD
          </h2>
          <ul className="space-y-3">
            {[
              { icon: ShieldCheck, t: "Every install carries a 12-month workmanship warranty in writing" },
              { icon: Wrench, t: "Heat-shrunk crimp terminations, ANL fusing at both ends, factory-look loom routing" },
              { icon: Wrench, t: "Correctly-sized cable per REDARC / manufacturer spec — never guesswork" },
              { icon: ShieldCheck, t: "Written quote before any work starts — no surprises at pickup" },
              { icon: MapPin, t: "26+ completed builds documented publicly with photos on our website" },
            ].map((s, i) => (
              <li
                key={i}
                data-testid={`about-standard-${i}`}
                className="flex items-start gap-3 barlow text-white/80"
              >
                <s.icon className="w-5 h-5 text-cyan-pz mt-0.5 flex-shrink-0" />
                <span>{s.t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-[#0A0A0A] py-14">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-6">
            READY TO BOOK YOUR VEHICLE IN?
          </h2>
          <div data-testid="about-euro-caution" className="flex items-start gap-3 border border-amber-400/40 bg-amber-400/5 p-4 mb-6 max-w-2xl">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-amber-100/80 barlow text-sm leading-relaxed">{EURO_CAUTION}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={`tel:${SITE.business.phoneTel}`}
              data-testid="about-cta-call"
              className="inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-6 py-4 bebas text-base tracking-[0.2em] hover:bg-white transition-colors"
            >
              <Phone className="w-4 h-4" /> CALL {SITE.business.phone}
            </a>
            <QuoteButton variant="small" testId="about-cta-email" />
            <Link
              to="/builds"
              data-testid="about-cta-builds"
              className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-6 py-4 bebas text-base tracking-[0.2em] hover:border-cyan-pz hover:text-cyan-pz transition-colors"
            >
              SEE COMPLETED BUILDS <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
